<template>
    <div
        v-if="isVisible"
        class="mb-8 rounded-xl bg-rent-risk-blue-100/80 px-6 py-5"
    >
        <div class="flex items-start justify-between gap-4">
            <div>
                <h3 class="text-lg font-semibold text-gray-900">{{ title }}</h3>
                <p class="mt-1 text-sm text-gray-600">
                    {{ description }}
                </p>
            </div>
            <button
                type="button"
                class="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-white/70 hover:text-gray-600"
                aria-label="Dismiss how it works"
                @click="dismiss"
            >
                <font-awesome-icon :icon="faXmark" class="h-4 w-4" aria-hidden="true" />
            </button>
        </div>

        <div class="relative mt-6 hidden items-center lg:flex lg:justify-between lg:gap-2">
            <template v-for="(step, index) in steps" :key="step.title">
                <div class="flex min-w-0 flex-1 items-center gap-3 px-2">
                    <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-rent-risk-blue-400 shadow-sm ring-1 ring-rent-risk-blue-400/10">
                        <font-awesome-icon
                            v-if="step.icon"
                            :icon="step.icon"
                            class="h-4 w-4"
                            aria-hidden="true"
                        />
                        <span v-else class="text-sm font-semibold">{{ index + 1 }}</span>
                    </div>
                    <div class="min-w-0">
                        <p class="text-sm font-semibold text-gray-900">{{ step.title }}</p>
                        <p class="mt-0.5 text-xs leading-relaxed text-gray-600">{{ step.description }}</p>
                    </div>
                </div>
                <div
                    v-if="index < steps.length - 1"
                    class="flex shrink-0 items-center self-center px-1 text-gray-400"
                    aria-hidden="true"
                >
                    <font-awesome-icon :icon="faArrowRight" class="h-4 w-4" />
                </div>
            </template>
        </div>

        <div class="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:hidden">
            <div
                v-for="(step, index) in steps"
                :key="`mobile-${step.title}`"
                class="flex items-center gap-3 rounded-lg bg-white/70 p-3"
            >
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-rent-risk-blue-400 shadow-sm ring-1 ring-rent-risk-blue-400/10">
                    <font-awesome-icon
                        v-if="step.icon"
                        :icon="step.icon"
                        class="h-4 w-4"
                        aria-hidden="true"
                    />
                    <span v-else class="text-sm font-semibold">{{ index + 1 }}</span>
                </div>
                <div class="min-w-0">
                    <p class="text-sm font-semibold text-gray-900">{{ step.title }}</p>
                    <p class="mt-0.5 text-xs leading-relaxed text-gray-600">{{ step.description }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faXmark, faArrowRight } from '@fortawesome/pro-light-svg-icons'

const props = defineProps({
    title: {
        type: String,
        default: 'How it works',
    },
    description: {
        type: String,
        required: true,
    },
    steps: {
        type: Array,
        required: true,
    },
    storageKey: {
        type: String,
        required: true,
    },
})

const isDismissed = () => {
    const value = localStorage.getItem(props.storageKey)

    return value === 'true' || value === '1'
}

const isVisible = ref(typeof window !== 'undefined' && !isDismissed())

const dismiss = () => {
    isVisible.value = false

    if (typeof window !== 'undefined') {
        localStorage.setItem(props.storageKey, 'true')
    }
}
</script>
